import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { useUiStore } from '@/store/useUiStore';
import Counter from '@/components/Counter';
import './Ch4Solutions.css';

interface Stats {
  delivered: number;
  inDev: number;
}
interface Family {
  name: string;
  items: string[];
}
interface Pillar {
  num: string;
  title: string;
  badge: string;
  stats: Stats;
  items?: string[];
  targets?: string[];
  families?: Family[];
  qualities?: string[];
  productFamilies?: Family[];
}

export default function Ch4Solutions() {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  const pillars = t('solutions.pillars', { returnObjects: true }) as Pillar[];
  const [active, setActive] = useState(0);
  const p = pillars[active];

  return (
    <section
      id="solutions"
      className="chapter chapter--tint chapter-solutions"
      aria-labelledby="solutions-title"
      onMouseEnter={() => setActiveChapter('solutions')}
    >
      <div className="chapter-inner">
        <div className="chapter-head chapter-head--center">
          <p className="section-kicker">{t('solutions.kicker')}</p>
          <h2 id="solutions-title" className="chapter-title">
            {t('solutions.title')}
          </h2>
        </div>

        {/* pillar switcher */}
        <div className="sol-tabs" role="tablist" aria-label={t('solutions.title')}>
          {pillars.map((pil, i) => (
            <button
              key={pil.num}
              role="tab"
              aria-selected={active === i}
              className="sol-tab"
              data-active={active === i}
              onClick={() => setActive(i)}
            >
              <span className="sol-tab-num">{pil.num}</span>
              <span className="sol-tab-title">{pil.title}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            className="sol-panel"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="sol-panel-head">
              <div>
                <span className="sol-panel-num">{p.num}</span>
                <h3 className="sol-panel-title">{p.title}</h3>
                <span className="sol-badge">{p.badge}</span>
              </div>
              <div className="sol-stats">
                <div className="sol-stat">
                  <Counter to={p.stats.delivered} className="sol-stat-num" />
                  <span className="sol-stat-label">{t('solutions.delivered')}</span>
                </div>
                <div className="sol-stat sol-stat--dev">
                  <Counter to={p.stats.inDev} className="sol-stat-num" />
                  <span className="sol-stat-label">{t('solutions.inDev')}</span>
                </div>
              </div>
            </div>

            {/* pillar-specific content */}
            {p.items && (
              <div className="sol-two">
                <div className="sol-block">
                  <div className="sol-items">
                    {p.items.map((it) => (
                      <span key={it} className="chip">
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
                {p.targets && (
                  <div className="sol-block sol-targets">
                    {p.targets.map((tg) => (
                      <div key={tg} className="sol-target">
                        {tg}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {p.families && (
              <div className="grid-2 sol-families">
                {p.families.map((f) => (
                  <div key={f.name} className="card sol-family">
                    <h4 className="card-title">{f.name}</h4>
                    <div className="sol-family-items">
                      {f.items.map((it) => (
                        <span key={it} className="chip">
                          {it}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {p.productFamilies && (
              <>
                {p.qualities && (
                  <div className="sol-qualities">
                    {p.qualities.map((q) => (
                      <span key={q} className="chip chip--accent">
                        {q}
                      </span>
                    ))}
                  </div>
                )}
                <div className="grid-3 sol-products">
                  {p.productFamilies.map((f) => (
                    <div key={f.name} className="card sol-product">
                      <h4 className="card-title">{f.name}</h4>
                      <ul className="sol-product-list">
                        {f.items.map((it) => (
                          <li key={it}>{it}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
